import { useState, useRef, useEffect } from "react";
import { createAgentProxy, useAgentRPC } from "@mzhub/plexus";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const agent = createAgentProxy({
  agentId: "global",
  transport: "http",
  serverUrl: "/api/plexus",
});

function ChatMessage({ message }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
          isUser
            ? "bg-[--brand-frontend] text-[--oil-black] rounded-br-sm font-medium"
            : "bg-[--overlay] border border-[--border] text-[--text-body] rounded-bl-sm"
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

function StatusIndicator({ status }) {
  if (status === "idle") return null;

  const labels = {
    thinking: "Thinking...",
    responding: "Typing...",
    tool: "Using tool...",
    error: "Error occurred",
  };

  return (
    <div className="flex items-center gap-2 text-xs text-[--brand-accent] px-4 py-2">
      <Loader2 size={12} className="animate-spin" />
      {labels[status] || status}
    </div>
  );
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const { messages, sendMessage, status, reset } = useAgentRPC(agent);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || status !== "idle") return;

    const page = location.pathname.split("/")[1] || "home";
    sendMessage(input.trim(), { page });
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.role === "assistant" && lastMsg?.toolResults) {
      lastMsg.toolResults.forEach((result) => {
        if (result.data?.action === "navigate" && result.data?.url) {
          navigate(result.data.url);
        }
      });
    }
  }, [messages, navigate]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-[--brand-frontend] text-[--oil-black] rounded-full shadow-lg hover:brightness-110 transition-all"
        aria-label="Open chat"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-[--overlay] border-2 border-[--border] rounded-xl shadow-2xl flex flex-col max-h-[500px]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[--border] bg-[--brand-agent]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[--brand-accent] animate-pulse" />
          <span className="font-semibold text-sm text-[--text-on-agent]">
            Nimbus
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reset}
            className="text-xs text-[--text-on-agent]/70 hover:text-[--text-on-agent] transition-colors"
          >
            Clear
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1 hover:bg-[--tungsten] rounded transition-colors text-[--text-on-agent]"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
        {messages.length === 0 && (
          <div className="text-center text-[--text-muted] text-sm py-8">
            <p className="mb-2">Hi! I'm Plexus Assistant.</p>
            <p>Ask me about the framework or explore examples.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}
        <StatusIndicator status={status} />
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-[--border] bg-[--oil-black]">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={status !== "idle"}
            className="flex-1 bg-[--tungsten] border border-[--border] rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[--brand-frontend] focus:border-transparent disabled:opacity-50 transition-all"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || status !== "idle"}
            className="p-2 bg-[--brand-frontend] text-[--oil-black] rounded-lg hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
